import memoize from 'lodash/memoize'

import type { WorkflowFilterStatus } from '@app/shared/types/sf-data'
import type { PaasOperator } from './types'

/**
 * Get the active overview filter. Rails interpolates the filter in a hidden input field value.
 * @param fallback - The default filter to use if no filter is found
 * @returns WorkflowFilterStatus - The filter to use
 */
export const getOverviewFilter = (
  fallback = 'show-all'
): WorkflowFilterStatus => {
  const overview =
    window?.Silverfin?.currentUserPreferences?.overview_filter || {}
  return overview?.[window?.Silverfin?.currentCompanyId]?.filter || fallback
}

/**
 * Returns the current stack id from the page. Rails interpolates the stack id in a hidden input field value.
 * It's unlikely that this will change for the current user per page, so memoize it
 * @returns {string | null} The stack id or null if not found
 */
export const getStackId = memoize((): string | null => {
  const stackIdElement = document.querySelector(
    '#sf_stack_id'
  ) as HTMLInputElement

  return stackIdElement?.value || null
})

/**
 * Returns whether the current workflow is locked.
 * @returns {boolean}
 */
export const isCurrentWorkflowLocked = (): boolean => {
  return window?.Silverfin?.currentWorkflowLocked() || false
}

/**
 * Returns whether the current firm supports the given feature.
 * @param feature - The feature identifier to check
 * @returns { boolean } - Whether the feature is supported
 */
export const isFeatureSupported = (
  feature: keyof typeof window.Silverfin.currentFirmFeatures
): boolean | undefined => {
  return !!window?.Silverfin?.currentFirmFeatures?.[feature]
}

/**
 * Returns whether a LaunchDarkly feature flag is enabled for the current
 * request. The value is evaluated server-side (via FeatureServer) and exposed
 * on window.Silverfin.currentFeatureFlags.
 * @param flag - The flag identifier to check
 * @returns { boolean } - Whether the flag is enabled
 */
export const isFeatureFlagEnabled = (
  flag: keyof typeof window.Silverfin.currentFeatureFlags
): boolean => {
  return !!window?.Silverfin?.currentFeatureFlags?.[flag]
}

/**
 * Mirrors the Rails `ApplicationController#using_new_layout?` helper: the
 * firm must support the new layout AND either the firm has it forced on by
 * default or the user has opted in via "Switch now". Use this (rather than
 * the `supports_new_layout` firm feature) whenever behavior must match the
 * layout Rails actually rendered, since the firm flag alone can be on while
 * the old layout is still served.
 */
export const isUsingNewLayout = (): boolean => {
  if (!isFeatureSupported('supports_new_layout')) return false
  if (isFeatureSupported('supports_new_layout_on_by_default')) return true
  return !!window?.Silverfin?.currentUserPreferences?.use_new_layout
}

/**
 * Returns whether the current company is a personal company. Rails interpolates this as a boolean in a hidden input field value.
 */
export const isPersonalCompany = memoize((_companyId: string): boolean => {
  const personalCompanyIdElement = document.querySelector(
    '#current_company_personal'
  ) as HTMLInputElement

  return personalCompanyIdElement?.value === 'true'
})

// Absent input (no current ledger) → not private.
export const isCurrentPeriodPrivate = (): boolean => {
  const input = document.getElementById(
    'current_period_private'
  ) as HTMLInputElement | null

  return input?.value === 'true'
}

export const getCompanyName = (): string | null => {
  const companyName = document.querySelector(
    '#current_company_name'
  ) as HTMLInputElement

  return companyName?.value || null
}

export const getCurrentUserName = (): string | null => {
  const userName = document.querySelector(
    '#current_user_name'
  ) as HTMLInputElement

  return userName?.value || null
}

export const getCurrentUserId = (): string | null => {
  const userId = document.querySelector('#current_user_id') as HTMLInputElement

  return userId?.value || null
}

export const companyUrl = memoize((companyId: string): string => {
  return `/f/${window.Silverfin.currentFirmId}/${companyId}`
})

/**
 * Retrieves the current firm format for zero (can be '-' or '0')
 * This is useful for validation purposes and number conversion purposes
 * @returns - The character '-' or '0' based on the firm setting
 */
export const getFirmZeroFormat = memoize((_firmId: string): string => {
  const input = document.getElementById(
    'current_firm_currency_zero_format'
  ) as HTMLInputElement
  const format = input?.value

  if (!format || format !== '-') {
    return '0'
  }

  return '-'
})

export const getCurrentPaasOperatorName = (): PaasOperator | null => {
  const paasOperatorName = document.querySelector(
    '#current_paas_operator_name'
  ) as HTMLInputElement

  return paasOperatorName?.value || null
}

export const hasLeftDrawer = (): boolean => {
  return !!document.querySelector('#left-drawer')
}

export const hasRightDrawer = (): boolean => {
  return !!document.querySelector('#right-drawer')
}

/**
 * Whether this page renders the AI chat toggle, the only control that can close
 * a restored chat drawer again.
 */
export const isAiChatDrawerAvailable = (): boolean => {
  return (
    document.getElementById('right-drawer')?.dataset.aiChatAvailable === 'true'
  )
}
